import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { GlobalConstants } from './global-constants';

export interface ThemeColor {
  variable: string;
  color: string;
}

// The same rules the backend applies (`sanitizeThemeColors`), checked again
// here because these values are written straight onto :root, and localStorage
// can be edited by anything running on the page.
const VARIABLE_PATTERN = /^--color-[a-z0-9-]{1,48}$/;
const COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

const DEFAULT_KEY = 'themeDefault';
const USER_KEY = 'theme';

/**
 * The app's theme colours, kept in localStorage and applied to :root:
 *  - `themeDefault`: the saved default, for everyone.
 *  - `theme`: the signed-in user's theme: their OU's own if one of their OUs
 *    has one, otherwise the default. Applied over `themeDefault`.
 *
 * Both are set at login and refreshed on every app start, from
 * `ou/my-theme` when signed in or `ou/public-default-theme` when not, so a
 * theme saved since login applies without logging in again.
 */
@Injectable({
  providedIn: 'root'
})
export class ThemeService {

  // Settles once the startup refresh has finished, successfully or not. The
  // theme settings page waits for it before reading :root, so a late response
  // cannot overwrite the colours it is previewing.
  ready: Promise<void> = Promise.resolve();
  private pending: Promise<void> | null = null;

  constructor(private http: HttpClient) { }

  /**
   * Apply the stored theme at once so the first paint is right, then refresh it
   * from the server.
   */
  init(): Promise<void> {
    // On /login both the app and the login page call this at startup.
    if (this.pending) {
      return this.pending;
    }
    this.applyStored();
    this.pending = this.refresh().finally(() => this.pending = null);
    this.ready = this.pending;
    return this.ready;
  }

  /**
   * Fetch the current theme, store it, and apply it if it changed. Pass
   * `apply: false` to only update localStorage, e.g. while a page is
   * previewing other colours on :root.
   */
  async refresh(apply = true): Promise<void> {
    try {
      const { theme, themeDefault } = await this.fetch();
      if (!themeDefault.length) {
        return;
      }

      const defaultChanged = this.store(DEFAULT_KEY, themeDefault);
      const userChanged = this.isLoggedIn() && this.store(USER_KEY, theme);
      if (apply && (defaultChanged || userChanged)) {
        this.applyStored();
      }
    } catch {
      // Keep the stored or stylesheet colours; a theme is not worth an error.
    }
  }

  // The default first, then the user's theme over it. The default sets every
  // themed variable, so this also replaces what a previous user's OU theme left
  // on :root: logout does not reload the page.
  applyStored(): void {
    this.apply(this.stored(DEFAULT_KEY));
    if (this.isLoggedIn()) {
      this.apply(this.stored(USER_KEY));
    }
  }

  stored(key: typeof DEFAULT_KEY | typeof USER_KEY): ThemeColor[] {
    try {
      return this.clean(JSON.parse(localStorage.getItem(key) || 'null'));
    } catch {
      return [];
    }
  }

  clean(values: unknown): ThemeColor[] {
    if (!Array.isArray(values)) {
      return [];
    }
    return values
      .filter(item => typeof item?.variable === 'string' && typeof item?.color === 'string'
        && VARIABLE_PATTERN.test(item.variable) && COLOR_PATTERN.test(item.color))
      .map(item => ({ variable: item.variable, color: item.color }));
  }

  private apply(colors: ThemeColor[]): void {
    colors.forEach(item => document.documentElement.style.setProperty(item.variable, item.color));
  }

  // Sent through HttpClient rather than DataService: a failed call here should
  // leave the colours alone, not clear the session and redirect to login.
  private async fetch(): Promise<{ theme: ThemeColor[]; themeDefault: ThemeColor[] }> {
    if (this.isLoggedIn()) {
      const res: any = await firstValueFrom(this.http.get(GlobalConstants.apiURL + 'ou/my-theme'));
      return {
        theme: res?.success ? this.clean(res.data?.theme) : [],
        themeDefault: res?.success ? this.clean(res.data?.themeDefault) : [],
      };
    }
    const res: any = await firstValueFrom(this.http.get(GlobalConstants.apiURL + 'ou/public-default-theme'));
    return { theme: [], themeDefault: res?.success ? this.clean(res.data) : [] };
  }

  // Returns whether the stored value changed.
  private store(key: string, colors: ThemeColor[]): boolean {
    const next = JSON.stringify(colors);
    if (localStorage.getItem(key) === next) {
      return false;
    }
    localStorage.setItem(key, next);
    return true;
  }

  private isLoggedIn(): boolean {
    return localStorage.getItem('isLoggedIn') == 'true';
  }
}
